import type {
  DiversificationConstraint,
  Piece,
  PiecewiseCondition,
  PiecewiseConstraint,
} from '@/types/api/constraints'

import {
  DiversificationConstraintType,
  PiecewiseConstraintType,
} from '@moment/horizon-data-access/proposal'

/**
 * A single account-size tier of a tiered max-weight diversification
 * constraint. Weights are UI percentages (0-100); account bounds are dollar
 * amounts of account market value.
 */
export interface MaxWeightTier {
  id: string
  /** Upper bound of account market value for this tier; null for the final catch-all tier. */
  maxAccountValue: number | null
  /** Max weight per unique value, as a UI percentage (0-100). */
  maxWeight: number | undefined
  /**
   * Min weight per unique value (UI percentage), preserved through round-trip
   * for constraints authored elsewhere (e.g. classic model builder). Not
   * editable in this UI.
   */
  minWeight?: number
}

interface ParsedPiecewiseDiversification {
  field: string
  tiers: MaxWeightTier[]
}

function asNumber(value: string | number | undefined): number | undefined {
  return typeof value === 'number' ? value : undefined
}

function upperBound(condition: PiecewiseCondition): number | undefined {
  const amv = condition.account_market_value
  if (!amv) return undefined
  return (
    asNumber(amv.less_than_or_equal_to) ?? asNumber(amv.less_than) ?? undefined
  )
}

function lowerBound(condition: PiecewiseCondition): number | undefined {
  const amv = condition.account_market_value
  if (!amv) return undefined
  return (
    asNumber(amv.greater_than) ??
    asNumber(amv.greater_than_or_equal_to) ??
    undefined
  )
}

/**
 * Recognizes a PiecewiseConstraint that encodes account-size tiering of a
 * single diversification field ("max 10% per issuer below $250k, 5% above")
 * and converts it into tier state. Returns undefined for any other piecewise
 * shape — those keep the existing drop-on-load behavior.
 *
 * Accepted shape: every piece holds exactly one diversification constraint on
 * the same field with a max weight, conditioned only on account_market_value;
 * bounds are contiguous with exactly one unbounded catch-all piece.
 */
export function parsePiecewiseDiversification(
  constraint: PiecewiseConstraint,
): ParsedPiecewiseDiversification | undefined {
  const pieces = constraint.pieces
  if (!pieces || pieces.length < 2) return undefined

  let field: string | undefined
  const parsed: Array<{
    upper: number | undefined
    lower: number | undefined
    maxWeight: number
    minWeight: number | undefined
  }> = []

  for (const piece of pieces) {
    if (piece.constraints.length !== 1) return undefined
    const child = piece.constraints[0]
    if (child?.type !== DiversificationConstraintType.diversification)
      return undefined
    if (field === undefined) field = child.field
    if (child.field !== field) return undefined

    const maxWeight = child.max_weight_per_unique_value
    if (typeof maxWeight !== 'number') return undefined

    const upper = upperBound(piece.condition)
    const lower = lowerBound(piece.condition)
    if (
      !piece.condition.account_market_value ||
      (upper === undefined && lower === undefined)
    ) {
      return undefined
    }

    parsed.push({
      upper,
      lower,
      maxWeight: maxWeight * 100,
      minWeight:
        typeof child.min_weight_per_unique_value === 'number'
          ? child.min_weight_per_unique_value * 100
          : undefined,
    })
  }
  if (field === undefined) return undefined

  parsed.sort(
    (a, b) =>
      (a.upper ?? Number.POSITIVE_INFINITY) -
      (b.upper ?? Number.POSITIVE_INFINITY),
  )

  // Exactly one catch-all piece, sorted last; all other pieces bounded above
  // and contiguous with the previous bound.
  const unboundedCount = parsed.filter((p) => p.upper === undefined).length
  if (unboundedCount !== 1) return undefined

  for (const [i, piece] of parsed.entries()) {
    const isLast = i === parsed.length - 1
    if (!isLast && piece.upper === undefined) return undefined
    const previousUpper = i > 0 ? parsed[i - 1]?.upper : undefined
    if (piece.lower !== undefined && piece.lower !== (previousUpper ?? 0)) {
      return undefined
    }
    if (isLast && piece.lower === undefined) return undefined
  }

  return {
    field,
    tiers: parsed.map((piece, index) => ({
      id: `tier-${index}`,
      maxAccountValue: piece.upper ?? null,
      maxWeight: piece.maxWeight,
      ...(piece.minWeight !== undefined && { minWeight: piece.minWeight }),
    })),
  }
}

/**
 * Builds the PiecewiseConstraint for account-size-tiered max weight. Mirrors
 * the classic model builder's tier-to-piece conversion: each tier except the
 * last carries an upper account_market_value bound, the last is the
 * catch-all, and consecutive tiers share their boundary via greater_than.
 *
 * Scope is intentionally NOT set on the nested constraints — the optimizer
 * overrides every child's scope with the wrapper's scope on expansion, so the
 * wrapper (set on save in proposal-state-to-api) is the only place it counts.
 */
export function buildPiecewiseDiversification({
  field,
  tiers,
  priority,
}: {
  field: string
  tiers: MaxWeightTier[]
  priority: number
}): PiecewiseConstraint | undefined {
  // A tier set with any blank/zero weight is incomplete. Emitting only the
  // filled-in subset would silently shift the remaining tiers' account
  // bounds relative to what the UI shows, so no constraint is produced
  // until every tier has a weight.
  if (tiers.length < 2) return undefined
  if (
    tiers.some((tier) => tier.maxWeight === undefined || tier.maxWeight <= 0)
  ) {
    return undefined
  }
  // The last tier must be the catch-all (no upper bound).
  if (tiers[tiers.length - 1]?.maxAccountValue !== null) {
    return undefined
  }
  // Every non-last tier needs a positive ascending bound, otherwise a piece
  // would be emitted with an empty condition and constrain unconditionally.
  if (!validateMaxWeightTiers(tiers)) return undefined

  const pieces: Piece[] = tiers.map((tier, index) => {
    const isLastTier = index === tiers.length - 1
    const previousTierMax =
      index > 0 ? (tiers[index - 1]?.maxAccountValue ?? null) : 0

    const condition: PiecewiseCondition = {}
    if (!isLastTier && tier.maxAccountValue !== null) {
      condition.account_market_value = {
        less_than_or_equal_to: tier.maxAccountValue,
      }
      if (previousTierMax !== null && previousTierMax > 0) {
        condition.account_market_value.greater_than = previousTierMax
      }
    } else if (isLastTier && previousTierMax !== null) {
      condition.account_market_value = {
        greater_than: previousTierMax,
      }
    }

    const child: DiversificationConstraint = {
      type: DiversificationConstraintType.diversification,
      field,
      // guarded to defined, positive weights above
      max_weight_per_unique_value: (tier.maxWeight ?? 0) / 100,
      ...(tier.minWeight !== undefined && {
        min_weight_per_unique_value: tier.minWeight / 100,
      }),
      constraint_priority: priority,
    }

    return { condition, constraints: [child] }
  })

  return {
    type: PiecewiseConstraintType.piecewise,
    pieces,
    constraint_priority: priority,
  }
}

/**
 * Per-tier bound validation message, or undefined when the bound is valid.
 * The last (catch-all) tier carries no bound and never errors.
 */
export function getMaxWeightTierBoundError(
  tiers: MaxWeightTier[],
  index: number,
): string | undefined {
  if (index === tiers.length - 1) return undefined
  const current = tiers[index]?.maxAccountValue
  if (current === null || current === undefined) {
    return 'Account value is required'
  }
  if (current <= 0) return 'Must be greater than 0'
  if (index > 0) {
    const previous = tiers[index - 1]?.maxAccountValue
    if (previous !== null && previous !== undefined && current <= previous) {
      return 'Must be greater than previous tier'
    }
  }
  return undefined
}

/** Tiers validate when every bounded tier has a positive, strictly ascending bound. */
export function validateMaxWeightTiers(tiers: MaxWeightTier[]): boolean {
  return tiers.every(
    (_, index) => getMaxWeightTierBoundError(tiers, index) === undefined,
  )
}
